import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { User, CheckCircle, XCircle, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { uiLocale } from '@/i18n/locale'

interface User {
  id: string;
  email: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

interface UserStatusProps {
  currentUser: User | null;
  onLogout: () => void;
}

export function UserStatus({ currentUser, onLogout }: UserStatusProps) {
  const { t } = useTranslation();
  return (
    <Card className="bg-transparent border-none shadow-none">
      <CardHeader className="pt-8">
        <div className="flex justify-center">
          <User className="h-16 w-16" style={{ color: 'var(--color-auth-icon)' }} />
        </div>
        <CardTitle className="text-center text-2xl font-medium text-gray-800">
          {t('auth.userStatus.title')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {currentUser ? (
          <div className="space-y-6">
            <div className="flex items-center justify-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <span className="font-medium text-gray-800">{t('auth.userStatus.signedInAs')}</span>
              <Badge variant="secondary" className="bg-[var(--color-auth-badge-bg)] text-white">{currentUser.name}</Badge>
            </div>
            <div className="text-sm text-gray-600 space-y-2">
              <p><span className="font-medium">{t('auth.userStatus.email')}</span> {currentUser.email}</p>
              <p><span className="font-medium">{t('auth.userStatus.id')}</span> {currentUser.id}</p>
              <p><span className="font-medium">{t('auth.userStatus.registered')}</span> {(() => {
                try {
                  if (!currentUser.createdAt) {
                    return t('auth.userStatus.dateUnavailable');
                  }
                  
                  const date = new Date(currentUser.createdAt);
                  if (isNaN(date.getTime())) {
                    return t('auth.userStatus.dateUnavailable');
                  }
                  return date.toLocaleString(uiLocale(), { 
                    year: 'numeric', 
                    month: '2-digit', 
                    day: '2-digit', 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  });
                } catch (error) {
                  return t('auth.userStatus.dateUnavailable');
                }
              })()}</p>
            </div>
            <div className="flex justify-center pt-2">
              <Button 
                onClick={onLogout} 
                className="min-w-[220px] h-11 bg-[var(--color-auth-primary)] hover:bg-[var(--color-auth-primary-hover)] text-white uppercase tracking-wide shadow-md"
              >
                <LogOut className="mr-2 h-5 w-5" />
                {t('common.logout')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2">
            <XCircle className="h-5 w-5 text-red-500" />
            <span className="text-gray-600">{t('auth.userStatus.notSignedIn')}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
